import { FC, useEffect, useState } from "react";
import { Accordion } from "../../../components/controls/accordion"
import { v4 as uuidv4} from 'uuid';
import FlexColumn from "../../../components/controls/FlexColumn";
import { NumberInput } from "../../../components/controls/inputs/BaseInput";
import styles from "../AnalysisLot/Styles/AnalysisFilterLot.module.scss"
import { useTranslation } from "react-i18next";
import { useLocalStorageChange } from "../../../system/hooks/useLocalStorageChange";
import { IDataFilter, IJournalFilters, typeReport } from "../DashboardBusinessControls/interfaces";
import { DatePeriodPanel } from "../../../hoc/DatePeriodPanel/DatePeriodPanel";
import { IDatePeriod } from "../../../components/datePeriodPicker/datePeriodPicker";
import { DefaultDatePeriod } from "../../../system/hooks/useGridFilter";
import { DateTime } from "luxon";
import FlexRow from "../../../components/controls/FlexRow";
import { IOption, Select } from "../../../components/selects/select";

interface Props {
    onOpenAccordion: (name: TypeAccordionName) => void;
    setFilters:(filters: IAnalysisFilters) => void;
    onFiltersLoaded: () => void;
    currentAccordion?: TypeAccordionName;
    tabId?: string;    
}

export type TypeAccordionName = 'withoutSalesReport' | 'none'

interface IWithoutSalesReport {
    periodInDays: number
    salesCount: number //количество продаж
    compareSalesCount: IOption
    incomingCount: number //количество поступлений
    datePeriod: IDatePeriod
}

export interface IAnalysisFilters {    
    IsGnvls?: boolean,
    withoutSalesReport: IWithoutSalesReport
}

export const defaultAnalysisFilters = {
    withoutSalesReport: {
        periodInDays: 30,
        incomingCount: 0,
        salesCount: 0,
        compareSalesCount: {displayName: '=', value: 'Eq' as OperatorType}
    }
} as IAnalysisFilters

export const AnalyticalFilters: FC<Props> = ({
    onOpenAccordion,
    setFilters,
    onFiltersLoaded,
    tabId
}) => {
    const [activeAccordion, setActiveAccordion] = useState<TypeAccordionName>('none');
    const [savedFilters, setSavedFilters] = useLocalStorageChange<IJournalFilters>('journalFilters');
    
    const getDatePeriodForFilter = (type: typeReport): IDatePeriod => {    
        switch(type) {
            case typeReport.WithoutSalesReport:
                return {
                    startDate: DateTime.local(1900, 1, 1),
                    endDate: DateTime.now()
                };
            
            default:
                return DefaultDatePeriod;
        }
    };
    
    const processFilters = (filters: IJournalFilters) => {
        try {
            const updatedFilters = { ...defaultAnalysisFilters };
            let accordionToOpen: TypeAccordionName = 'none';

            updatedFilters.IsGnvls = filters.filters.IsGnvls;
            
            if (filters.reportType === typeReport.WithoutSalesReport) {
                updatedFilters.withoutSalesReport = {
                    ...updatedFilters.withoutSalesReport,
                    periodInDays: Number(filters.filters?.DashboardWithoutSalesReport?.PeriodInDays) || defaultAnalysisFilters.withoutSalesReport.periodInDays,
                    incomingCount: defaultAnalysisFilters.withoutSalesReport.incomingCount,
                    salesCount: defaultAnalysisFilters.withoutSalesReport.salesCount,
                    compareSalesCount: {
                        displayName:filters.filters?.DashboardWithoutSalesReport?.CompareSalesCount?.DisplayName ?? "=",
                        value : filters.filters?.DashboardWithoutSalesReport?.CompareSalesCount?.Value ?? "Eq"
                    },
                    datePeriod: getDatePeriodForFilter(typeReport.WithoutSalesReport)
                };
                accordionToOpen = 'withoutSalesReport';
                setActiveAccordion('withoutSalesReport');
            }
            setAnalysisFilters(updatedFilters);
            setFilters(updatedFilters);
            onOpenAccordion(accordionToOpen);
            setSavedFilters(null);
            localStorage.removeItem('journalFilters');
            onFiltersLoaded();
        } catch (error) {
            console.error(error);
        }
    };
    
    const [analysisFilters, setAnalysisFilters] = useState<IAnalysisFilters>(defaultAnalysisFilters);
    const { t } = useTranslation();
    const baseT = (value: string) => t('analysisLot.' + value)

    const defaultSearchOptions = [
            {displayName: '=', value: 'Eq' as OperatorType},
            {displayName: '>', value: 'More' as OperatorType},
            {displayName: '>=', value: 'MoreOrEq' as OperatorType},
            {displayName: '<', value: 'Less' as OperatorType},
            {displayName: '<=', value: 'LessOrEq' as OperatorType},
        ] as IOption[];

    const handleAccordionToggle = (name: TypeAccordionName, isOpen: boolean) => {
        setActiveAccordion(isOpen ? name : 'none');
        onOpenAccordion(isOpen ? name : 'none');
    };

    

    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters(analysisFilters);
        }, 200);
        return () => clearTimeout(timer);
    }, [analysisFilters]);

    useEffect(() => {
        if (!savedFilters || !tabId) return;

        if (savedFilters.tabId === tabId) {
            processFilters(savedFilters);           
            setSavedFilters(null);
        }
    }, [savedFilters, tabId]);

    return <>
        <h1>{baseT('analysisWithoutSales')}</h1>
        <Accordion 
            opened={activeAccordion === 'withoutSalesReport'}
            caption={baseT('withOutSalesFilters')} 
            id={`${uuidv4()}`}
            onOpen={(isOpen) => handleAccordionToggle('withoutSalesReport', isOpen)}        
        >
            <FlexColumn className={styles.flexColumn}>
                    <DatePeriodPanel
                        datePeriod={analysisFilters.withoutSalesReport.datePeriod}
                        label={baseT("analysisWithoutSalesLotDatePeriod")}
                        onDatePeriodChange={(date: IDatePeriod) => {
                            setAnalysisFilters({ ...analysisFilters, withoutSalesReport: { ...analysisFilters.withoutSalesReport, datePeriod: date } })
                        }}
                    />
                    <NumberInput
                        className={styles.numberInput} label={baseT('periodInDaysWithoutSales')}
                        value={analysisFilters.withoutSalesReport.periodInDays}
                        min={0} 
                        onChange={(value) => setAnalysisFilters({...analysisFilters, withoutSalesReport: {...analysisFilters.withoutSalesReport, periodInDays: value} })}                    
                    />
                    <NumberInput
                        className={styles.numberInput} label={baseT('incomingCount')}
                        value={analysisFilters.withoutSalesReport.incomingCount}
                        min={0} 
                        onChange={(value) => setAnalysisFilters({...analysisFilters, withoutSalesReport: {...analysisFilters.withoutSalesReport, incomingCount: value} })}                    
                    />
                    <FlexRow>
                        <NumberInput
                            className={styles.numberInput} label={baseT('salesCount')}
                            value={analysisFilters.withoutSalesReport.salesCount}
                            min={0} 
                            onChange={(value) => setAnalysisFilters({...analysisFilters, withoutSalesReport: {...analysisFilters.withoutSalesReport, salesCount: value} })}                    
                        />
                        <Select
                            label={baseT('compare')}
                            defaultOption={defaultSearchOptions[2]}
                            value={analysisFilters.withoutSalesReport.compareSalesCount}
                            options={defaultSearchOptions}
                            onSelect={(option) => setAnalysisFilters({ ...analysisFilters, withoutSalesReport: { ...analysisFilters.withoutSalesReport, compareSalesCount: option } })}
                        />
                    </FlexRow>

            </FlexColumn>
        </Accordion>
    </>
}